import React, { useState, useRef } from 'react';
import axios from 'axios';
import ReactSummernote from 'react-summernote';
import 'react-summernote/dist/react-summernote.css';
import sanitizeHtml from 'sanitize-html';

import 'bootstrap/js/src/modal';
import 'bootstrap/js/src/dropdown';
import 'bootstrap/js/src/tooltip';

import './summernote/audio';
import './summernote/audio.css';
import $ from 'jquery';

const uploadFile = (file, api) => {
  const fd = new FormData();

  fd.append('file', file);

  return axios({
    method: 'post',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: fd,
    ...api,
  });
};

class Note extends ReactSummernote {
  get callbacks() {
    return {
      ...super.callbacks,
      onAudioUpload: this.props.onAudioUpload,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    // disable file drop to contenteditables
    this.noteEditable.bind('dragover drop', (e) => {
      e.preventDefault();

      return false;
    });
  }
}

function createCustomButton(showYoutubeModal) {
  return function (context) {
    const { ui } = $.summernote;
    const button = ui.button({
      contents: '<i class="fa-brands fa-youtube"></i>',
      tooltip: 'youtube',
      click() {
        showYoutubeModal(context);
      },
    });
    return button.render();
  };
}

function YoutubeModal({ show, onClose, onSubmit }) {
  const [input, setInput] = useState('');
  return (
    <div
      className={`modal fade${show ? ' show' : ''}`}
      style={{ display: show ? 'block' : 'none' }}
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Insert YouTube Video</h5>
            <button type="button" className="close" onClick={onClose}>
              &times;
            </button>
          </div>
          <div className="modal-body">
            <input
              type="text"
              className="form-control"
              placeholder="Paste YouTube URL here"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                onSubmit(input);
                onClose();
              }}
            >
              Insert
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Summernote = ({
  value,
  onChange,
  onBlur,
  fileUploadUrl,
  toolbar = [
    ['style', ['style']],
    [
      'font',
      [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'superscript',
        'subscript',
        'color',
        'clear',
      ],
    ],
    ['para', ['ul', 'ol', 'paragraph']],
    ['table', ['table']],
    ['mybutton', ['myVideo']],
    ['insert', ['hr', 'link', 'video', 'picture', 'audio']],
    ['view', ['codeview', 'undo', 'redo']],
  ],
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalContext, setModalContext] = useState(null);

  // Callback to trigger modal from custom button
  const showYoutubeModal = (context) => {
    setModalContext(context);
    setShowModal(true);
  };

  // Insert video after modal submit
  const handleModalSubmit = (url) => {
    if (!modalContext || !url) return;
    const ytMatch = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/,
    );
    if (ytMatch && /^[A-Za-z0-9_-]{11}$/.test(ytMatch[1])) {
      const videoId = ytMatch[1];
      const iframe = document.createElement('iframe');
      iframe.width = '640';
      iframe.height = '360';
      iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}`;
      iframe.frameBorder = '0';
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.title = 'YouTube video player';
      modalContext.invoke('editor.insertNode', iframe);
    } else {
      // fallback: insert as clickable link
      const a = document.createElement('a');
      a.href = url;
      a.textContent = url;
      a.target = '_blank';
      modalContext.invoke('editor.insertNode', a);
    }
    setShowModal(false);
    setModalContext(null);
  };

  return (
    <>
      <Note
        onInit={function init(note) {
          note.replace(value);
        }}
        onChange={onChange}
        onBlur={onBlur}
        options={{
          disableDragAndDrop: true,
          dialogsInBody: true,
          styleTags: ['p', 'blockquote', 'h2', 'h3', 'h4', 'h5', 'h6'],
          toolbar,
          buttons: {
            myVideo: createCustomButton(showYoutubeModal),
          },
        }}
        onPaste={(e) => {
          const clipboard = (e.originalEvent || e).clipboardData || window.clipboardData;
          const html = clipboard.getData('text/html');
          const text = clipboard.getData('text');

          e.preventDefault();

          if (html) {
            const toPaste = sanitizeHtml(html, {
              allowedAttributes: {
                '*': ['style'],
                a: ['href', 'name', 'target'],
                img: ['src'],
              },
              allowedStyles: {
                '*': {
                  color: [
                    /^#(0x)?[0-9a-f]+$/i,
                    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
                  ],
                  'text-align': [/^left$/, /^right$/, /^center$/, /^justify$/],
                },
              },
            });

            document.execCommand('insertHTML', false, toPaste);
          } else {
            document.execCommand('insertText', false, text);
          }
        }}
        onImageUpload={async (files, insertImage) => {
          const res = await uploadFile(files[0], { url: fileUploadUrl });
          const { url } = res.data;

          await new Promise((r) => setTimeout(r, 1000));

          insertImage(url);
        }}
        onAudioUpload={async (files, insertAudio) => {
          const res = await uploadFile(files[0], { url: fileUploadUrl });
          const { url } = res.data;

          await new Promise((r) => setTimeout(r, 1000));

          insertAudio(url);
        }}
      />
      <YoutubeModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setModalContext(null);
        }}
        onSubmit={handleModalSubmit}
      />
    </>
  );
};

export default Summernote;
